import {
  type GetJobProgressionResponse,
  type JobProgressionRealtimeEvent as JobProgressionRealtimeEventProto,
  type JobProgressionStep as JobProgressionStepProto,
  EstimatedTaxPaymentInput,
  EstimatedTaxPaymentStateInput,
  JobProgressionExtensionType as JobProgressionExtensionTypeProto,
  JobProgressionFlow as JobProgressionFlowProto,
  JobProgressionStepStatus as JobProgressionStepStatusProto,
  OverrideableJobProgressionStepStatus as OverrideableJobProgressionStepStatusProto,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { Id } from '@/infra/id'
import { numberToDecimal, timestampToDate } from '@/utils/grpc-util'
import { match } from '@/utils/pattern-match-util'
import type {
  EstimatedTaxPayment,
  ExtensionProgressionFlow,
  JobProgressionFlow,
  JobProgressionStepStatus,
  JobProgressionProps,
  JobProgressionStep,
  OverrideableJobProgressionStepStatus,
} from './JobProgression'

export function mapJobProgressionResponseProtoToProps(
  proto: GetJobProgressionResponse,
  jobId: Id,
): JobProgressionProps {
  return {
    id: Id(proto.getId()),
    jobId,
    steps: proto
      .getProgressionStepsList()
      .map(mapJobProgressionStepProtoToProps),
    progressionFlow: mapJobProgressionFlowProtoToJobProgressionFlow(
      proto.getProgressionFlow(),
    ),
  }
}

export function mapJobProgressionRealtimeEventProtoToProps(
  proto: JobProgressionRealtimeEventProto,
): JobProgressionProps {
  return {
    id: Id(proto.getId()),
    jobId: Id(proto.getJobId()),
    steps: proto
      .getProgressionStepsList()
      .map(mapJobProgressionStepProtoToProps),
    progressionFlow: mapJobProgressionFlowProtoToJobProgressionFlow(
      proto.getProgressionFlow(),
    ),
  }
}

export function mapExtensionProgressionFlowToProto(
  flow: ExtensionProgressionFlow,
): JobProgressionExtensionTypeProto {
  return match(flow, {
    Personal: JobProgressionExtensionTypeProto.PERSONAL,
    Business: JobProgressionExtensionTypeProto.BUSINESS,
    Outsourcing: JobProgressionExtensionTypeProto.OUTSOURCING_EXTENSION_TYPE,
  })
}

export function mapEstimatedTaxPaymentToProto(
  payment: EstimatedTaxPayment,
): EstimatedTaxPaymentInput {
  return new EstimatedTaxPaymentInput()
    .setFederalAmount(numberToDecimal(payment.federalAmount))
    .setAmountByStateList(
      payment.amountByState.map((s) =>
        new EstimatedTaxPaymentStateInput()
          .setState(s.state)
          .setAmount(numberToDecimal(s.amount)),
      ),
    )
    .setIsCalculated(payment.isCalculated)
}

export function mapOverrideableJobProgressionStepStatusToProto(
  step: OverrideableJobProgressionStepStatus,
): OverrideableJobProgressionStepStatusProto {
  return match(step, {
    ReturnFiled: OverrideableJobProgressionStepStatusProto.ADMIN_RETURN_FILED,
    JobStarted: OverrideableJobProgressionStepStatusProto.ADMIN_JOB_STARTED,
    OpenItems: OverrideableJobProgressionStepStatusProto.ADMIN_OPEN_ITEMS,
  })
}

function mapJobProgressionStepProtoToProps(
  proto: JobProgressionStepProto,
): JobProgressionStep {
  const response = proto.getResponse()
  return {
    id: proto.getId(),
    status: mapJobProgressionStepStatusProtoToStatus(
      proto.getProgressionStep(),
    ),
    response:
      response === JobProgressionStepStatusProto.PROGRESSION_STEP_UNSPECIFIED
        ? null
        : mapJobProgressionStepStatusProtoToStatus(response),
    dateCreated: timestampToDate(proto.getDateCreated()),
    dateResponded: timestampToDate(proto.getDateResponded()),
    createdByUserPublicId: proto.getCreatedByUserPublicId() || undefined,
  }
}

export function mapJobProgressionStepStatusProtoToStatus(
  proto: JobProgressionStepStatusProto,
): JobProgressionStepStatus {
  switch (proto) {
    case JobProgressionStepStatusProto.PROGRESSION_STEP_UNSPECIFIED:
      return 'Unspecified'
    case JobProgressionStepStatusProto.JOB_STARTED:
      return 'JobStarted'
    case JobProgressionStepStatusProto.OPEN_ITEMS:
      return 'OpenItems'
    case JobProgressionStepStatusProto.DRAFT_IN_REVIEW:
      return 'DraftInReview'
    case JobProgressionStepStatusProto.DRAFT_REJECTED:
      return 'DraftRejected'
    case JobProgressionStepStatusProto.DRAFT_APPROVED:
      return 'DraftApproved'
    case JobProgressionStepStatusProto.NOT_STARTED:
      return 'NotStarted'
    case JobProgressionStepStatusProto.AUTHORIZATION_REQUESTED:
      return 'AuthorizationRequested'
    case JobProgressionStepStatusProto.AUTHORIZATION_SIGNED:
      return 'AuthorizationSigned'
    case JobProgressionStepStatusProto.RETURN_FILED:
      return 'ReturnFiled'
    case JobProgressionStepStatusProto.DELIVERABLE_COMPLETED:
      return 'DeliverableCompleted'
    case JobProgressionStepStatusProto.PREPARATION_COMPLETED:
      return 'PreparationCompleted'
    case JobProgressionStepStatusProto.REVIEW_COMPLETED:
      return 'ReviewCompleted'
    case JobProgressionStepStatusProto.EXTENSION_IN_PROGRESS:
      return 'ExtensionInProgress'
    case JobProgressionStepStatusProto.EXTENSION_CONFIRMED:
      return 'ExtensionConfirmed'
    case JobProgressionStepStatusProto.EXTENSION_ACCEPTED:
      return 'ExtensionAccepted'
    case JobProgressionStepStatusProto.EXTENSION_REJECTED:
      return 'ExtensionRejected'
    case JobProgressionStepStatusProto.PROGRESSION_WAITING_ON_CLIENT:
      return 'ProgressionWaitingOnClient'
    case JobProgressionStepStatusProto.READY_TO_FILE:
      return 'ReadyToFile'
  }
}

function mapJobProgressionFlowProtoToJobProgressionFlow(
  proto: JobProgressionFlowProto,
): JobProgressionFlow {
  switch (proto) {
    case JobProgressionFlowProto.NONE:
      return 'None'
    case JobProgressionFlowProto.RETAIL:
      return 'Retail'
    case JobProgressionFlowProto.OUTSOURCING:
      return 'Outsourcing'
    case JobProgressionFlowProto.ESSENTIAL:
      return 'Essential'
    case JobProgressionFlowProto.PREPARATION:
      return 'Preparation'
    case JobProgressionFlowProto.REVIEW:
      return 'Review'
    case JobProgressionFlowProto.PERSONAL_EXTENSION:
      return 'PersonalExtension'
    case JobProgressionFlowProto.BUSINESS_EXTENSION:
      return 'BusinessExtension'
    case JobProgressionFlowProto.OUTSOURCING_EXTENSION:
      return 'OutsourcingExtension'
  }
}
